/**
 * Imperative label font loading — the offscreen rasterizer has no React
 * tree, so it can't use `useFont`. Loads the SAME Nunito SemiBold face the
 * editor canvas uses (via `useFloorTheme`) so preview === print glyphs.
 *
 * Compiler-excluded (`services/`).
 *
 * @module services/labelTemplates/fonts
 * @category Services
 */

import { Asset } from 'expo-asset';
import type { SkFont, SkTypeface } from '@shopify/react-native-skia';
import { Skia } from '@shopify/react-native-skia';

import NunitoSemiBold from '@assets/fonts/NunitoSemiBold.ttf';

const state: {
  typeface: SkTypeface | null;
  loading: Promise<SkTypeface> | null;
} = { typeface: null, loading: null };

const loadTypeface = async (): Promise<SkTypeface> => {
  const asset = Asset.fromModule(NunitoSemiBold);
  await asset.downloadAsync();
  const uri = asset.localUri ?? asset.uri;
  const data = await Skia.Data.fromURI(uri);
  const typeface = Skia.Typeface.MakeFreeTypeFaceFromData(data);
  if (typeface === null) {
    throw new Error('labelTemplates: failed to load label typeface');
  }
  state.typeface = typeface;
  return typeface;
};

/** Load (once) and return the label typeface. */
const getLabelTypeface = async (): Promise<SkTypeface> => {
  if (state.typeface !== null) return state.typeface;
  if (state.loading === null) {
    state.loading = loadTypeface();
  }
  return state.loading;
};

/** A fresh SkFont at `size` — drawers re-size it per label anyway. */
export const makeLabelFont = async (size = 14): Promise<SkFont> => {
  const typeface = await getLabelTypeface();
  return Skia.Font(typeface, size);
};
